/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import {
  editBooking,
  getAgentCreditCards,
  getBookingAvailableStatus,
  getBookingItemAvailableStatus,
  getBookingTemporary,
  getBookingTemporaryContractors,
  getBookingTemporaryItem,
  getBookingTemporaryItemAttributes,
  getBookingTemporaryItemTradeTerms,
  getFormOfPayments,
  getTemporaryContractorBookingMonit,
  getTemporaryFile,
  newBooking,
  postBookingSave,
  postCheckExportOrderBetweenOffice,
  postCreateBookingVoucher,
  postUpdateBookingTemporary,
  postUploadTemporaryFile,
  previewBooking,
  setBookingSelectionState,
} from "@/requests/booking";
import { BookingGetTemporaryResponse } from "@/models/api/bookings/BookingGetTemporaryResponse";
import {
  AttributeItem,
  BookingGetTemporaryItem,
  BookingGetTemporaryItemResponse,
  TradeTermItem,
} from "@/models/api/bookings/BookingGetTemporaryItem";
import { BookingGetTemporaryContractorsResponse } from "@/models/api/bookings/BookingGetTemporaryContractorsResponse";
import BaseCommand from "@/models/api/commands/BaseCommand";
import { RELOAD_TYPES } from "@/static/reloadTypes";
import BookingAddEditCommandsModule from "@/store/modules/bookings/bookingAddEdit/commands";
import { TemporaryFileResponse } from "@/models/api/bookings/TemporaryFile";
import { VOUCHER_ATTRIBUTES } from "@/static/voucherAttributes";
import {
  CommandResponseBookingItemTmpId,
  CommandResponseCanMergeBookings,
  OperationResult,
} from "@/models/api/orders/OperationResult";
import i18n from "@/plugins/i18n";
import { filterCapitalize } from "@/filters/capitalize";
import { ConfirmCustomButton, ConfirmModel } from "@/models/components/popup/Confirm";
import { translatedStatuses } from "@/static/bookingStatuses";
import { VOUCHER_TYPE } from "@/static/voucherTypes";
import { getOpenTemporaryFile } from "@/requests/general";
import { filterFormatDate } from "@/filters/formatDate";
import routerInstance from "@/plugins/router";
import { SelectItem } from "@/models/api/SelectItem";
import { getTradeTermsForBooking } from "@/requests/serviceType";
import AgentCreditCard from "@/models/api/bookings/AgentCreditCard";
import { OfficeProfileFormOfPayment } from "@/models/api/administration/offices/OfficeProfileFormOfPayment";
import { Monit } from "@/models/components/popup/Monit";
import { BookingMonit } from "@/models/api/bookings/ContractorMonit";
import { CONTRACTOR_TYPES } from "@/static/contractorTypes";

export interface BookingAddEditStateInterface {
  filter: Array<CriterionBase>;
  sessionId: null | string;
  operationId: null | string;
  booking: null | BookingGetTemporaryResponse;
  bookingItems: null | BookingGetTemporaryItemResponse;
  contractors: null | BookingGetTemporaryContractorsResponse;
  bookingItemAttributes: Array<AttributeItem>;
  bookingItemTradeTerms: Array<TradeTermItem>;
  bookingId: null | number;
  previewBookingId: null | number;
  files: TemporaryFileResponse | null;
  isVoucherAttributesFilter: boolean;
  isRequiredAttributesFilter: boolean;
  currentBookingItemId: string | null;
  isFileUploading: boolean;
  voucherType: VOUCHER_TYPE;
  isCreateVoucher: boolean;
  isUnsavedChanges: boolean;
  updateBookingTemporaryQuery: Array<BaseCommand>;
  isUpdateBookingTemporaryProcessing: boolean;
  isTouristModule: boolean;
  tradeTermServices: Array<SelectItem>;
  formOfPayments: Array<OfficeProfileFormOfPayment>;
  agentCreditCards: Array<AgentCreditCard>;
  loader: {
    id: number | undefined;
    isViewLoading: boolean;
  };
  bookingMonit: Monit;
  isCalcDialog: boolean;
  attributesTableHeight: number;
}

export const BookingsAddEditState: BookingAddEditStateInterface = {
  filter: [],
  sessionId: null,
  operationId: null,
  booking: null,
  bookingItems: null,
  contractors: null,
  bookingItemAttributes: [],
  bookingItemTradeTerms: [],
  bookingId: null,
  previewBookingId: null,
  files: null,
  isVoucherAttributesFilter: false,
  isRequiredAttributesFilter: false,
  isUnsavedChanges: false,
  currentBookingItemId: null,
  isFileUploading: false,
  voucherType: VOUCHER_TYPE.HotelStandard_PL,
  isCreateVoucher: false,
  updateBookingTemporaryQuery: Array<BaseCommand>(),
  isUpdateBookingTemporaryProcessing: false,
  tradeTermServices: [],
  formOfPayments: [],
  agentCreditCards: [],
  isTouristModule: false,
  loader: {
    id: undefined,
    isViewLoading: false,
  },
  bookingMonit: {
    items: [],
  },
  isCalcDialog: false,
  attributesTableHeight: 560,
};

const BookingAddEditModule = defineModule({
  modules: {
    commands: BookingAddEditCommandsModule,
  },
  state: BookingsAddEditState as BookingAddEditStateInterface,
  namespaced: true as const,
  getters: {
    filteredAttributes(...args): Array<AttributeItem> {
      const { state } = _moduleGetterContext(args);
      const result = new Array<AttributeItem>();
      const isHidden = (attribute: AttributeItem): boolean => {
        if (!attribute.isVisible) {
          return true;
        }
        if (state.isRequiredAttributesFilter && !attribute.isRequired) {
          return true;
        }
        // noinspection RedundantIfStatementJS
        if (state.isVoucherAttributesFilter && !VOUCHER_ATTRIBUTES.includes(attribute.attributePrototypeId ?? -1)) {
          return true;
        }
        return false;
      };
      for (const attribute of state.bookingItemAttributes) {
        attribute.isHidden = isHidden(attribute);
        result.push(attribute);
      }
      //return state.isDocumentsLoading || state.isDocumentPaymentsLoading;
      result.sort((a, b) => (a.ordinal > b.ordinal ? 1 : -1));
      return result;
    },
    bookingType(...args): null | string {
      const { state } = _moduleGetterContext(args);
      return state.bookingItems?.bookingItemTmp?.serviceTypeGroupCode ?? null;
    },
    isPreview(...args): boolean {
      const { state } = _moduleGetterContext(args);
      return state.previewBookingId !== null;
    },
  },
  mutations: {
    resetState(state) {
      state.filter = [];
      state.sessionId = null;
      state.operationId = null;
      state.booking = null;
      state.bookingItems = null;
      state.contractors = null;
      state.bookingItemAttributes = [];
      state.bookingItemTradeTerms = [];
      state.bookingId = null;
      state.previewBookingId = null;
      state.files = null;
      state.isVoucherAttributesFilter = false;
      state.isRequiredAttributesFilter = false;
      state.isUnsavedChanges = false;
      state.currentBookingItemId = null;
      state.isFileUploading = false;
      state.voucherType = VOUCHER_TYPE.HotelStandard_PL;
      state.isCreateVoucher = false;
      state.updateBookingTemporaryQuery = Array<BaseCommand>();
      state.isUpdateBookingTemporaryProcessing = false;
      state.tradeTermServices = [];
      state.formOfPayments = [];
      state.agentCreditCards = [];
      state.isTouristModule = false;
      state.loader = {
        id: undefined,
        isViewLoading: false,
      };
      state.isCalcDialog = false;
    },
    setSessionId(state, param: string | null) {
      state.sessionId = param;
    },
    setOperationId(state, param: string | null) {
      state.operationId = param;
    },
    setBooking(state, param: null | BookingGetTemporaryResponse) {
      state.booking = param;
    },
    setBookingItem(state, param: null | BookingGetTemporaryItemResponse) {
      state.bookingItems = param;
      if (state.currentBookingItemId === null && param !== null && param.bookingItemTmp.tmpId !== null) {
        state.currentBookingItemId = param.bookingItemTmp.tmpId;
      }
    },
    setContractors(state, param: null | BookingGetTemporaryContractorsResponse) {
      state.contractors = param;
    },
    setAttributes(state, param: Array<AttributeItem>) {
      state.bookingItemAttributes = param;
    },
    setTradeTerms(state, param: Array<TradeTermItem>) {
      state.bookingItemTradeTerms = param;
    },
    setFiles(state, param: TemporaryFileResponse) {
      state.files = param;
    },
    setBookingId(state, param: null | number) {
      state.bookingId = param;
    },
    setIsVoucherAttributesFilter(state, param: boolean) {
      state.isVoucherAttributesFilter = param;
    },
    setIsRequiredAttributesFilter(state, param: boolean) {
      state.isRequiredAttributesFilter = param;
    },
    setBookingItemId(state, param: null | string) {
      state.currentBookingItemId = param;
    },
    setIsFileUploading(state, param: boolean) {
      state.isFileUploading = param;
    },
    setVoucherType(state, param: VOUCHER_TYPE) {
      state.voucherType = param;
    },
    setIsCreateVoucher(state, param: boolean) {
      state.isCreateVoucher = param;
    },
    setPreviewBookingId(state, param: number | null) {
      state.previewBookingId = param;
    },
    setIsUpdateBookingTemporaryProcessing(state, param: boolean) {
      state.isUpdateBookingTemporaryProcessing = param;
    },
    setUnsavedChanges(state, param: boolean) {
      state.isUnsavedChanges = param;
    },
    setTradeTermServices(state, param: Array<SelectItem>) {
      state.tradeTermServices = param;
    },
    setAgentCreditCards(state, param: Array<AgentCreditCard>) {
      state.agentCreditCards = param;
    },
    setFormOfPayments(state, param: Array<OfficeProfileFormOfPayment>) {
      state.formOfPayments = param;
    },
    setIsTouristModule(state, param: boolean) {
      state.isTouristModule = param;
    },
    setIsViewLoading(state, param: boolean) {
      state.loader.isViewLoading = param;
    },
    setLoaderId(state, param: number | undefined) {
      state.loader.id = param;
    },
    clearBookingMonit(state) {
      state.bookingMonit = {
        items: [],
      };
    },
    setBookingMonit(state, param: { id: number; data: BookingMonit | null }) {
      if (!param.data || !param.id) return;

      // Clear state before add new items
      state.bookingMonit.items = state.bookingMonit.items.filter((x) => x.id != param.id);

      // Add items
      state.bookingMonit.items.push({
        id: param.id,
        title: param.data.title ?? "",
        content: param.data.content ?? "",
        isVisible: param !== null,
      });

      // Order by id
      state.bookingMonit.items = state.bookingMonit.items.sort((a, b) => a.id - b.id);
    },
    removeBookingMonit(state, id: number) {
      state.bookingMonit.items = state.bookingMonit.items.filter((x) => x.id != id);
    },
    setMonitState(state, param: boolean) {
      state.bookingMonit.items.forEach((item) => (item.isVisible = param));
    },
    setIsCalcDialog(state, param: boolean) {
      state.isCalcDialog = param;
    },
    setAttributesTableHeight(state, param: number) {
      state.attributesTableHeight = param;
    },
  },
  actions: {
    async addLoader(context) {
      const { commit } = _moduleActionContext(context);
      const timeout: null | number = setTimeout(() => {
        commit.setIsViewLoading(true);
      }, 100);
      commit.setLoaderId(timeout);
    },
    async removeLoader(context) {
      const { commit, state } = _moduleActionContext(context);
      clearTimeout(state.loader.id);
      commit.setIsViewLoading(false);
      commit.setLoaderId(undefined);
    },
    async clear(context) {
      const { commit } = _moduleActionContext(context);
      commit.setOperationId(null);
      commit.setSessionId(null);
      commit.setBookingItemId(null);
      commit.setContractors(null);
      commit.setBooking(new BookingGetTemporaryResponse());
      commit.setBookingItem({
        bookingItemTmp: new BookingGetTemporaryItem(),
        bookingItemIds: [],
      } as BookingGetTemporaryItemResponse);
      commit.setAttributes([]);
      commit.setFiles([]);
    },
    async newBooking(context) {
      const { dispatch, commit } = _moduleActionContext(context);
      dispatch.clear();
      const sessionId = await newBooking();
      if (typeof sessionId === "string") {
        commit.setSessionId(sessionId);
        commit.setOperationId(sessionId);
        commit.setPreviewBookingId(null);
        commit.setBookingId(null);
        commit.setUnsavedChanges(false);
        // noinspection ES6MissingAwait
        await dispatch.downloadAll();
      } else {
        await routerInstance.push({ name: "Bookings" });
      }
    },
    async editBooking(context, bookingId: number) {
      const { dispatch, commit } = _moduleActionContext(context);
      dispatch.clear();
      commit.setIsViewLoading(true);
      const sessionId = await editBooking(bookingId).catch(() => {
        commit.setIsViewLoading(false);
      });
      if (typeof sessionId === "string") {
        commit.setSessionId(sessionId);
        commit.setOperationId(sessionId);
        commit.setPreviewBookingId(null);
        commit.setBookingId(bookingId);
        commit.setUnsavedChanges(false);
        // noinspection ES6MissingAwait
        await dispatch.downloadAll();
      } else {
        await routerInstance.push({ name: "Bookings" });
      }
    },
    async previewBooking(context, bookingId: number) {
      const { dispatch, commit } = _moduleActionContext(context);
      dispatch.clear();
      commit.setPreviewBookingId(bookingId);
      const sessionId = await previewBooking(bookingId).catch(() => {
        commit.setIsViewLoading(false);
      });
      if (typeof sessionId === "string") {
        commit.setSessionId(sessionId);
        commit.setOperationId(sessionId);
        commit.setBookingId(bookingId);
        // noinspection ES6MissingAwait
        await dispatch.downloadAll();
      } else {
        commit.setPreviewBookingId(null);
      }
    },
    async cloneBooking(context, sessionId: string) {
      const { dispatch, commit } = _moduleActionContext(context);
      dispatch.clear();
      commit.setSessionId(sessionId);
      commit.setOperationId(sessionId);
      commit.setBookingId(null);
      commit.setUnsavedChanges(false);
      // noinspection ES6MissingAwait
      await dispatch.downloadAll();
    },
    async downloadAll(context) {
      const { dispatch, rootDispatch, state } = _moduleActionContext(context);
      // await dispatch.addLoader();

      await Promise.all([
        rootDispatch.attributes.downloadPrototypes(),
        rootDispatch.downloadGDS(),
        dispatch.getTradeTermServices(),
      ]);

      await Promise.all([dispatch.getTemporaryContractors(), dispatch.getTemporaryBooking()]);

      if (state.booking && state.booking.gdsId)
        await Promise.all([rootDispatch.serviceTypes.downloadServiceTypesCategorized(state.booking.gdsId)]);

      await Promise.all([dispatch.getTemporaryBookingItem(), dispatch.getTemporaryContractorMonit()]);

      await Promise.all([
        dispatch.getFormOfPayments(),
        dispatch.getTemporaryBookingItemAttributes(),
        dispatch.getTemporaryBookingItemTradeTerms(),
        dispatch.getTemporaryBookingFiles(),
      ]);

      await Promise.all([
        dispatch.getAgentCreditCards(),
        rootDispatch.cashiersAndTraders.downloadCashiersWithCode(state.bookingItems?.bookingItemTmp.agentId),
      ]);

      // await dispatch.removeLoader();
    },
    async getTemporaryContractors(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId !== null) {
        const response = await getBookingTemporaryContractors(state.operationId);
        if (response === null) {
          commit.setContractors(new BookingGetTemporaryContractorsResponse());
        } else {
          commit.setContractors(response);
        }
      }
    },
    async getTemporaryBooking(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId !== null) {
        const response = await getBookingTemporary(state.operationId);
        commit.setBooking(response);
      }
    },
    async getTemporaryBookingItem(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId !== null) {
        const response = await getBookingTemporaryItem(state.operationId, state.currentBookingItemId);
        commit.setBookingItem(response);
      }
    },
    async getTemporaryBookingItemAttributes(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId !== null) {
        const attributes = await getBookingTemporaryItemAttributes(state.operationId, state.currentBookingItemId);
        commit.setAttributes(attributes);
      }
    },
    async getTemporaryBookingItemTradeTerms(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId !== null) {
        const tradeTerms = await getBookingTemporaryItemTradeTerms(state.operationId, state.currentBookingItemId);
        commit.setTradeTerms(tradeTerms);
      }
    },
    async getTemporaryBookingFiles(context) {
      //eslint-disable-next-line
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId !== null) {
        const files = await getTemporaryFile(state.operationId);
        commit.setFiles(files);
      }
    },
    async queryUpdateBookingTemporary(context, command: BaseCommand) {
      const { state, dispatch, commit } = _moduleActionContext(context);
      state.updateBookingTemporaryQuery.push(command);
      commit.setUnsavedChanges(true);
      if (!state.isUpdateBookingTemporaryProcessing) {
        // noinspection ES6MissingAwait
        await dispatch.updateBookingTemporary();
      }
    },
    async updateBookingTemporary(context): Promise<void> {
      //eslint-disable-next-line
      const { commit, state, dispatch } = _moduleActionContext(context);
      if (state.updateBookingTemporaryQuery.length > 0) {
        commit.setIsUpdateBookingTemporaryProcessing(true);
        try {
          const command = state.updateBookingTemporaryQuery.shift() as BaseCommand;
          command.OperationId = state.operationId as string;
          command.SessionId = state.sessionId as string;
          const updateBookingTemporaryResponse = await postUpdateBookingTemporary(command);
          await dispatch.processResponse(updateBookingTemporaryResponse);
        } finally {
          commit.setIsUpdateBookingTemporaryProcessing(false);
          if (state.updateBookingTemporaryQuery.length > 0) {
            // noinspection ES6MissingAwait
            dispatch.updateBookingTemporary();
          }
        }
      }
    },
    async processResponse(context, param: OperationResult) {
      const { rootDispatch, dispatch, commit, state } = _moduleActionContext(context);
      commit.setOperationId(param.operationId);
      if (param.commandResponse !== null) {
        if (param.commandResponse instanceof CommandResponseBookingItemTmpId) {
          commit.setBookingItemId(param.commandResponse.bookingItemTmpId);
        } else if (param.commandResponse instanceof CommandResponseCanMergeBookings) {
          const popupContent = i18n
            .t("selectedBookingNumberExist", {
              recordLocator: param.commandResponse.recordLocator,
              date: filterFormatDate(param.commandResponse.createdAt),
            })
            .toString();
          const customButtons = [
            {
              text: filterCapitalize(i18n.t("keepSeparated").toString()),
              value: false,
              color: "warning",
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("assignToExisting").toString()),
              value: true,
              color: "warning",
            },
          ] as Array<ConfirmCustomButton>;
          const mergeBookings = await rootDispatch.popup.addConfirm(
            new ConfirmModel(popupContent, filterCapitalize(i18n.t("warning").toString()), customButtons, "700px")
          );
          if (mergeBookings) {
            await dispatch.commands.connectWithPreviousBooking();
          }
        }
      }
      for (const reload of param.reload) {
        if (reload === RELOAD_TYPES.Contractor) {
          await dispatch.getTemporaryContractors();
        } else if (reload === RELOAD_TYPES.BookingItem) {
          await dispatch.getTemporaryBookingItem();
        } else if (reload === RELOAD_TYPES.BookingItemAttribute) {
          await dispatch.getTemporaryBookingItemAttributes();
        } else if (reload === RELOAD_TYPES.File) {
          await dispatch.getTemporaryBookingFiles();
        } else if (reload === RELOAD_TYPES.Booking) {
          await dispatch.getTemporaryBooking();
          if (state.booking && state.booking.gdsId)
            await Promise.all([rootDispatch.serviceTypes.downloadServiceTypesCategorized(state.booking.gdsId)]);
        } else if (reload === RELOAD_TYPES.BookingItemTradeTerm) {
          await dispatch.getTemporaryBookingItemTradeTerms();
        } else if (reload === RELOAD_TYPES.AgentCreditCards) {
          await dispatch.getAgentCreditCards();
        } else if (reload === RELOAD_TYPES.BookingContractorMonit) {
          await dispatch.getTemporaryContractorMonit();
        } else if (reload === RELOAD_TYPES.BookingFormOfPayments) {
          await dispatch.getFormOfPayments();
        }
      }
    },
    async save(context): Promise<number | null> {
      const { rootDispatch, state, dispatch, commit } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.operationId !== null) {
        await dispatch.validateBookingStatus(true);
        await rootDispatch.waitForCompleteAllRequests();
        if (state.isCreateVoucher) {
          await dispatch.createBookingVoucher();
        }
        // Check should exportOrderBetweenOffice before save
        const showExportOrderBetweenOfficeConfirmDialog = await postCheckExportOrderBetweenOffice(state.operationId);
        if (showExportOrderBetweenOfficeConfirmDialog) {
          await dispatch.showExportOrderBetweenOfficeConfirmDialog();
        }
        await rootDispatch.waitForCompleteAllRequests();

        // SaveBooking
        const bookingId = await postBookingSave(state.operationId);
        if (typeof bookingId === "undefined") {
          return Promise.reject("Save failed");
        }
        commit.setUnsavedChanges(false);
        if (state.bookingId) {
          dispatch.setBookingSelectionState(false);
        }
        return bookingId;
      }
      return null;
    },
    async showExportOrderBetweenOfficeConfirmDialog(context) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const isConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("doYouWantToExportOrderBetweenOffice")),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "550px"
        )
      )) as boolean;
      await dispatch.commands.setContractorExportOrderBetweenOffice(isConfirmed);
    },
    async changeBookingItemId(context, param: string): Promise<void> {
      const { commit, dispatch, getters, rootDispatch, state } = _moduleActionContext(context);

      if (!getters.isPreview) {
        commit.setIsViewLoading(true);
        await dispatch.validateBookingItemStatus().catch(() => {
          commit.setIsViewLoading(false);
        });
      }
      commit.setBookingItemId(param);

      // await dispatch.addLoader();

      await Promise.all([
        dispatch.getTemporaryBookingItem(),
        dispatch.getTemporaryBookingItemAttributes(),
        dispatch.getTemporaryBookingItemTradeTerms(),
      ]);

      await Promise.all([
        dispatch.getFormOfPayments(),
        rootDispatch.cashiersAndTraders.downloadCashiersWithCode(state.bookingItems?.bookingItemTmp.agentId),
        dispatch.getAgentCreditCards(),
      ]);

      // await dispatch.removeLoader();
    },
    async uploadTemporaryFile(context, param: { files: Array<File>; type: number }) {
      const { commit, state, dispatch } = _moduleActionContext(context);
      commit.setIsFileUploading(true);
      const response = await postUploadTemporaryFile(state.operationId as string, param.type, param.files);
      commit.setIsFileUploading(false);
      await dispatch.processResponse(response);
    },
    async validateBookingItemStatus(context): Promise<void> {
      const { state, rootDispatch } = _moduleActionContext(context);
      const response = await getBookingItemAvailableStatus(
        state.operationId as string,
        state.bookingItems?.bookingItemTmp.tmpId as string
      );
      if (response.isAvailableStatusDifferentFromActualState) {
        const statusName = translatedStatuses.find((t) => t.value === response.availableBookingItemStatus)?.text;
        const changeStatus = await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(
              i18n.t("bookingItemMeetsRequirementsPartialReadyDoYouWantToChangeStatusToStatus", { statusName })
            ),
            filterCapitalize(i18n.t("information")),
            [
              {
                text: filterCapitalize(i18n.t("no")),
                value: false,
                color: "warning",
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("yes")),
                value: true,
                color: "warning",
              },
            ]
          )
        );
        if (changeStatus) {
          await rootDispatch.bookingAddEdit.commands.changeStatus(response.availableBookingItemStatus);
        }
      }
    },
    async validateBookingStatus(context, cancellable: boolean | undefined = undefined): Promise<void> {
      const { state, rootDispatch } = _moduleActionContext(context);
      cancellable = cancellable ?? false;
      const response = await getBookingAvailableStatus(state.operationId as string);
      if (response.length > 0) {
        let text = "";
        for (const changeStatus of response) {
          const index = state.bookingItems?.bookingItemIds.findIndex(
            (t) => t.id === changeStatus.bookingItemTmpId
          ) as number;
          text =
            text +
            "<br/>" +
            (index + 1) +
            " - " +
            translatedStatuses.find((t) => t.value === changeStatus.availableBookingItemStatus)?.text;
        }
        const customButtons: ConfirmCustomButton[] = [];
        if (cancellable) {
          customButtons.push({
            text: filterCapitalize(i18n.t("cancel")),
            value: null,
            color: "primary",
            outlined: true,
            insertSpace: true,
          });
        }
        customButtons.push({
          text: filterCapitalize(i18n.t("no")),
          value: false,
          color: "warning",
          outlined: true,
        });
        customButtons.push({
          text: filterCapitalize(i18n.t("yes")),
          value: true,
          color: "warning",
        });
        const changeStatus = await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("doYouWantToChangeStatusOfBookingItems").toString() + text),
            filterCapitalize(i18n.t("information")),
            customButtons,
            "550px"
          )
        );
        if (changeStatus === null) {
          throw new Error("Action cancelled by user");
        }
        if (changeStatus) {
          for (const changeStatus of response) {
            await rootDispatch.bookingAddEdit.commands.changeBookingItemStatus({
              value: changeStatus.availableBookingItemStatus,
              bookingItemTmpId: changeStatus.bookingItemTmpId,
            });
          }
        }
      }
    },
    async createBookingVoucher(context): Promise<void> {
      const { state, rootDispatch } = _moduleActionContext(context);
      if (state.operationId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        const files = await postCreateBookingVoucher(state.operationId, state.voucherType as VOUCHER_TYPE);
        await rootDispatch.bookingAddEdit.commands.addFiles({ fileIds: files, type: 10 });
        for (const file of files) {
          // noinspection ES6MissingAwait
          getOpenTemporaryFile(file);
        }
      }
    },
    async createVoucherForCurrentItem(context): Promise<void> {
      const { state, rootDispatch } = _moduleActionContext(context);
      if (state.operationId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        const files = await postCreateBookingVoucher(
          state.operationId,
          state.voucherType as VOUCHER_TYPE,
          state.currentBookingItemId
        );
        await rootDispatch.bookingAddEdit.commands.addFiles({ fileIds: files, type: 10 });
        for (const file of files) {
          // noinspection ES6MissingAwait
          getOpenTemporaryFile(file);
        }
      }
    },
    async getTradeTermServices(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getTradeTermsForBooking();
      commit.setTradeTermServices(response);
    },
    async getFormOfPayments(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.bookingItems && state.booking) {
        const response = await getFormOfPayments(
          state.bookingItems.bookingItemTmp.serviceTypeCode,
          state.booking.gdsId
        );
        commit.setFormOfPayments(response);
      }
    },
    async getAgentCreditCards(context) {
      const { state, commit } = _moduleActionContext(context);
      const response = await getAgentCreditCards(state.bookingItems?.bookingItemTmp.agentId ?? null);
      commit.setAgentCreditCards(response);
    },
    async getTemporaryContractorMonit(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId !== null) {
        commit.removeBookingMonit(1);

        const monits = await getTemporaryContractorBookingMonit(state.operationId);

        monits.forEach((monit) => {
          if (monit.type == CONTRACTOR_TYPES.Buyer) {
            commit.setBookingMonit({ id: 1, data: monit });
          }
          if (monit.type == CONTRACTOR_TYPES.Payer) {
            commit.setBookingMonit({ id: 2, data: monit });
          }
        });
      }
    },
    async toggleMonit(context) {
      const { state, commit } = _moduleActionContext(context);
      commit.setMonitState(!state.bookingMonit.items[0].isVisible);
    },
    async setBookingSelectionState(context, bookingState: boolean) {
      const { state } = _moduleActionContext(context);
      if (state.bookingId != null) {
        await setBookingSelectionState(state.bookingId, bookingState);
      }
    },
  },
});

export default BookingAddEditModule;

/* eslint-disable */
// @ts-ignore
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, BookingAddEditModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, BookingAddEditModule);
